/**
 * The kiosk's own line, because the kiosk has no header to put a badge in.
 *
 * It is a full-width bar rather than a badge, and it says the whole sentence
 * rather than a number, because of who is standing in front of it. The header
 * badge is read by somebody who works here and knows what a queue is. This is
 * read across a lobby, by a receptionist glancing over, and — the case that
 * decides the design — by a visitor who has just been told their check-in has
 * not reached anyone. A red "2" would mean nothing to them.
 *
 * It sits at the BOTTOM. The top of a kiosk screen is where the check-in flow
 * puts its own headings, and a bar above those would push the form down on the
 * one screen size we cannot control.
 */

import { useState } from 'react';
import { ChevronUpIcon, CloudArrowUpIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import PendingQueuePanel from '@/components/Common/PendingQueuePanel';
import { usePendingQueue } from '@/components/Common/usePendingQueue';

/**
 * "1 check-in", "2 check-outs", "1 check-in and 1 check-out" — what the items
 * ARE. Deep-test EXT-52: every item was called a check-in, so a refused
 * CHECK-OUT read "1 check-in was not accepted", which sends reception looking
 * for an arrival that never failed. The kiosk queues exactly these two; the
 * label the kiosk gives each capture says which ("Check-out — badge …").
 */
export function kioskItemsPhrase(items: { label: string; endpoint?: string }[]): { phrase: string; plural: boolean } {
  const outs = items.filter((i) => /^check-out/i.test(i.label) || /\/checkout$/.test(i.endpoint ?? '')).length;
  const ins = items.length - outs;
  const part = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;
  const parts = [ins ? part(ins, 'check-in') : '', outs ? part(outs, 'check-out') : ''].filter(Boolean);
  return { phrase: parts.join(' and '), plural: items.length !== 1 };
}

/**
 * `place`: the kiosk (a screen of its own, bar at the very bottom), or the front
 * desk (Reception C34: in the app, so it clears the phone's bottom bar, and the
 * person reading it IS reception).
 */
export default function PendingQueueLine({ place = 'kiosk' }: { place?: 'kiosk' | 'desk' } = {}) {
  const { waiting, blocked } = usePendingQueue();
  const [open, setOpen] = useState(false);
  const total = waiting.length + blocked.length;

  if (total === 0) return null;

  const needsPerson = blocked.length > 0;
  const blockedSay = kioskItemsPhrase(blocked);
  const allSay = kioskItemsPhrase([...waiting, ...blocked]);

  return (
    <>
      {/* A spacer the same height as the bar, in normal flow. The bar itself
          is fixed so it stays put on a tall screen, and without this it would
          sit on top of whatever the page put at its own bottom edge — on the
          kiosk, the version line. */}
      <div aria-hidden="true" className="h-[52px] w-full shrink-0" />

      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="pending-queue-line"
        className={`fixed inset-x-0 ${place === 'desk' ? 'bottom-14 lg:bottom-0 lg:left-1/2 lg:right-auto lg:-translate-x-1/2 lg:rounded-t-lg' : 'bottom-0'} z-40 flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold shadow-lg ${
          needsPerson
            ? 'bg-danger-600 text-white'
            : 'bg-warning-500 text-warning-950'
        }`}
        data-no-print
      >
        {needsPerson
          ? <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
          : <CloudArrowUpIcon className="h-5 w-5 shrink-0" />}
        {/* The live region is the TEXT, not the button. role="status" on the
            button itself would replace its button role, and a screen-reader
            user would be told the sentence but not that it can be opened. */}
        <span role="status">
          {needsPerson ? (
            <>
              {blockedSay.phrase} {blockedSay.plural ? 'were' : 'was'} not
              accepted{place === 'desk' ? ' — open to see why' : ' — please tell reception'}
            </>
          ) : (
            <>
              {allSay.phrase} {allSay.plural ? 'are' : 'is'} {place === 'desk'
                ? 'saved on this device, will send'
                : 'saved on this kiosk and not yet sent'}
            </>
          )}
        </span>
        <ChevronUpIcon className="h-4 w-4 shrink-0 opacity-70" />
      </button>

      <PendingQueuePanel open={open} onClose={() => setOpen(false)} />
    </>
  );
}
